import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, LoaderCircle, Users } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { RudderstackBrand } from '@/_shared/components/RudderstackBrand';
import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import { useAuth } from '@/_shared/hooks/useAuth';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { orpc } from '@/_shared/lib/orpc';

function InviteShell({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top_left,rgba(16,94,213,0.1),transparent_26%),linear-gradient(180deg,#f8fbff_0%,#f4f7fc_100%)] px-4">
      <div className="w-full max-w-sm animate-fade-in rounded-[1.75rem] border border-border/80 bg-white/92 p-8 text-center shadow-[0_28px_60px_-38px_rgba(47,66,112,0.38)] backdrop-blur-xl">
        <RudderstackBrand className="mb-5 justify-center" label="Rudder AI" badge="beta" />
        {children}
      </div>
    </div>
  );
}

export function AcceptInvite(): React.ReactElement {
  usePageTitle('Accept Invite');
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  const { data, isLoading, error } = useQuery(
    orpc.workspace.getInviteInfo.queryOptions({
      input: { inviteId: inviteId! },
    }),
  );

  const handleAccept = useCallback(async () => {
    try {
      const res = await fetch('/api/workspace/accept-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ inviteId }),
      });

      const body = (await res.json()) as { ok?: boolean; error?: string; workspace?: { id: string } };
      if (!res.ok) {
        throw new Error(body.error ?? 'Failed to accept invite');
      }

      await queryClient.invalidateQueries();
      const target = body.workspace?.id ? `/w/${body.workspace.id}` : '/';
      void navigate(target, { replace: true });
    } catch (err) {
      console.error('Failed to accept invite:', err);
    }
  }, [inviteId, queryClient, navigate]);

  // If already a member, auto-accept (which switches workspace via new JWT)
  useEffect(() => {
    if (data?.alreadyMember && user) {
      void handleAccept();
    }
  }, [data?.alreadyMember, user, handleAccept]);

  const handleSignIn = useCallback(() => {
    // Store invite ID in sessionStorage for post-login redirect
    if (inviteId) {
      sessionStorage.setItem('pending_invite', inviteId);
    }
    window.location.href = '/auth/github';
  }, [inviteId]);

  if (authLoading || isLoading) {
    return (
      <InviteShell>
        <div className="flex flex-col items-center gap-3 py-8">
          <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Preparing invite…</p>
        </div>
      </InviteShell>
    );
  }

  if (error || !data) {
    return (
      <InviteShell>
        <p className="mb-8 text-sm text-muted-foreground">Powered by RudderStack (private beta)</p>
        <div className="rounded-2xl border border-border/80 bg-white/70 p-6">
          <CircleAlert size={32} className="mx-auto mb-4 text-muted-foreground" />
          <h2 className="mb-2 text-lg font-semibold text-foreground">Invite Not Found</h2>
          <p className="text-sm text-muted-foreground">This invite link may be invalid or has been removed.</p>
        </div>
      </InviteShell>
    );
  }

  // Error states
  if (data.expired || data.revoked || data.full) {
    const message = data.expired
      ? 'This invite link has expired. Ask the workspace owner for a new link.'
      : data.revoked
        ? 'This invite has been revoked.'
        : 'This invite has reached its maximum uses.';

    return (
      <InviteShell>
        <p className="mb-8 text-sm text-muted-foreground">Powered by RudderStack (private beta)</p>
        <div className="rounded-2xl border border-border/80 bg-white/70 p-6">
          <CircleAlert size={32} className="mx-auto mb-4 text-amber-500" />
          <h2 className="mb-2 text-lg font-semibold text-foreground">Invite Unavailable</h2>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <p className="mb-8 text-sm text-muted-foreground">Powered by RudderStack (private beta)</p>
      <div className="rounded-2xl border border-border/80 bg-white/70 p-6">
        <Users size={32} className="mx-auto mb-4 text-brand" />
        <h2 className="mb-1 text-lg font-semibold text-foreground">Join {data.workspaceName}</h2>
        <div className="mb-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Avatar className="size-5">
            <AvatarImage src={data.inviterAvatar ?? undefined} alt={data.inviterLogin} />
            <AvatarFallback>{data.inviterLogin[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <span>Invited by {data.inviterLogin}</span>
        </div>

        {user ? (
          <button
            onClick={() => void handleAccept()}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border/90 bg-white/80 px-4 py-2.5 text-sm text-foreground transition-colors hover:border-brand hover:text-brand"
          >
            Accept Invite
          </button>
        ) : (
          <button
            onClick={handleSignIn}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border/90 bg-white/80 px-4 py-2.5 text-sm text-foreground transition-colors hover:border-brand hover:text-brand"
          >
            Sign in with GitHub to join
          </button>
        )}
      </div>
    </InviteShell>
  );
}
